import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { FamilleLivrablesCreateRequest } from '../requests/famille-livrables-create-request';
import { FamilleLivrablesStore } from '../store/famille-livrables.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-familles-livrables',
  imports: [
    ButtonAddNewComponent,
    HlmCardImports,
    ReactiveFormsModule,
    HlmFieldImports,
    HlmInputImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-familles-livrables.component.html',
})
export class FormFamillesLivrablesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #familleLivrablesStore = inject(FamilleLivrablesStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly form = this.#fb.group({
    familles: this.#fb.array([]),
  });

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();

  ngOnInit(): void {
    this.addFamille();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addFamille() {
    const famille = this.#fb.group({
      nom: [null, [Validators.required]],
    });
    this.familles.push(famille);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouvellesFamilles: FamilleLivrablesCreateRequest = {
      idReferentiel: this.referentiel().id,
      familles: this.familles.value.filter((x: { nom: string }) => !!x.nom?.trim()),
    };

    if (!nouvellesFamilles.familles || nouvellesFamilles.familles.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#familleLivrablesStore.créer({
      request: nouvellesFamilles,
      next: () => {
        this.#familleLivrablesStore.charger({
          idCartographie: this.cartographie().id,
          idRéférentiel: this.referentiel().id,
        });
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des familles réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouvellesFamilles.familles),
    });
  }

  alreadyExists(
    nouvellesFamilles: {
      nom: string;
    }[],
  ) {
    const toutesLesFamilles = this.#familleLivrablesStore.entities().map((x) => x.nom);

    const doublonsAvecFamillesExistantes = toutesLesFamilles.filter((x) =>
      nouvellesFamilles.map((y) => y.nom).includes(x),
    );

    this.familles.controls.forEach((control) => {
      if (
        doublonsAvecFamillesExistantes.includes(control.value.nom) ||
        this.familles.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get familles() {
    return this.form.get('familles') as FormArray;
  }
}
